<script lang="ts">
  import { page } from "$app/stores";
  import { parseTournamentName } from "$lib/services/tournaments/nameUtils";
  import { PUBLIC_TOURNAMENT_API } from "$env/static/public";
  import GamePage from "$lib/admin/components/blocks/blocks/Tournaments/GamePage.svelte";
  import FinishedTournament from "$lib/admin/components/blocks/blocks/Tournaments/FinishedTournament.svelte";
  import { isAuthenticated, user, authReady, project } from "$lib/services/store";
  import { isTournamentsRoute } from "$lib/services/utils";
  import Spinner from "$lib/main/components/Spinner.svelte";
  import type { Writable } from "svelte/store";
  import type { User } from "@/src/types/users";
  import type { Tournament } from "@/src/types/tournaments";
  import Nav from "$lib/main/Nav/index.svelte";

  const userStore: Writable<User | null> = user;

  let username: string = "";
  let userId: number;
  let tournamentName: string;
  let tournamentId: number;
  let sponsoredBy: number;
  let tournamentToplist: number;
  let tournamentIsLive: boolean;
  let tournamentAsset: string;
  let loading: boolean = true;
  let errorMessage: string;
  let userTournamentStatus: string;

  $: slug = $page.url.pathname.split("/")[2];
  $: currentTournamentPath = $project.settings.tournamentsUrl ?? "turneringar";
  $: isCurrentTournamentRoute = isTournamentsRoute($page.url.pathname, currentTournamentPath);

  const fetchTournamentData = async (username: string) => {
    try {
      const tournamentIdParsed = parseInt(slug.split("-")[0], 10);

      if (isNaN(tournamentIdParsed)) throw new Error("Invalid tournament ID");

      const headers: HeadersInit = {
        "Content-Type": "application/json",
        ...(username && { username }),
      };

      const response = await fetch(`${PUBLIC_TOURNAMENT_API}/${tournamentIdParsed}`, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error("Tournament not found");
      }

      const tournament: Tournament = await response.json();
      const parsedData = parseTournamentName(tournament.name);

      tournamentName = parsedData.name;
      tournamentId = tournament.tournamentId;
      sponsoredBy = parsedData.sponsoredBy || 13;
      tournamentToplist = parsedData.tournamentToplist || 9;
      tournamentIsLive = tournament.isLive;
      tournamentAsset = tournament.backgroundImage;
      userTournamentStatus = tournament.actionButton.label;
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : "An error occurred while loading the tournament.";
    } finally {
      loading = false;
    }
  };

  $: if ($authReady) {
    const currentUser = $userStore;
    if ($isAuthenticated && currentUser?.username) {
      username = currentUser.username;
      userId = currentUser.id || 0;
      fetchTournamentData(username);
    } else {
      fetchTournamentData("");
    }
  }
</script>

<head>
  <title>{tournamentName}</title>
</head>
<Nav project={$project} />
<div class="single-page" class:dark-mode={isCurrentTournamentRoute} class:sidebar={$project.settings?.sidebar}>
  {#if !$authReady || loading}
    <div class="container">
      <div class="loading-container">
        <Spinner
          spinColor={isCurrentTournamentRoute ? "#ffffff" : "#007bff"}
          textColor={isCurrentTournamentRoute ? "#cccccc" : "#666666"}
          text="Turnering laddas..." />
      </div>
    </div>
  {:else if errorMessage}
    <div class="container">
      <p class="error-message">{errorMessage}</p>
    </div>
  {:else if tournamentIsLive}
    <GamePage
      {tournamentName}
      {tournamentId}
      {sponsoredBy}
      {tournamentToplist}
      {tournamentIsLive}
      {tournamentAsset}
      {userTournamentStatus} />
  {:else}
    <FinishedTournament {tournamentId} />
  {/if}
</div>

<style>
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    background-color: rgba(0, 0, 0, 0.05);
  }

  .error-message {
    color: #e74c3c;
    font-size: 18px;
    text-align: center;
    padding: 20px;
  }

  .dark-mode .loading-container {
    background-color: rgba(255, 255, 255, 0.05);
  }
</style>
